import { diff } from 'just-diff'
import { diffApply } from 'just-diff-apply'
import { z, type ZodSchema } from 'zod'

import type { createAccess } from '@/database/access'
import { deleteLocalData, getLocalData, setLocalData } from '@/database/utility'

enum DiffOp {
  Remove = 'remove',
  Replace = 'replace',
  Add = 'add',
}

const diffRecordSchema = z.object({
  op: z.nativeEnum(DiffOp),
  path: z.union([z.string(), z.number()]).array(),
  value: z.any(),
})

/** Record of document diff after an update */
type DiffRecord = z.infer<typeof diffRecordSchema>

enum MutationOp {
  Add = 'add',
  Update = 'update',
  Delete = 'delete',
}

/** Record of document mutations inside a collection */
type MutationRecord = {
  action: MutationOp
  /** Value is either a DiffRecord or an Initialized Object for an add or null for a delete */
  value?: any
}

type CollectionChangeRecord = Record<string, MutationRecord>

const collectionChangeRecordSchema: ZodSchema<CollectionChangeRecord> =
  z.record(
    z.string(),
    z.object({
      action: z.nativeEnum(MutationOp),
      value: z.any(),
    }),
  )

/** Change records keyed by collectionPath  */
export type LocalStorageChangeRecord = Record<string, CollectionChangeRecord>

const changelogSchema = z.record(z.string(), collectionChangeRecordSchema)

const MAX_POLL_INTERVAL = 10000 // ms
const INIT_POLL_INTERVAL = 200 // ms
const POLL_INTERVAL_GROWTH = 2 // Growth factor
const MAX_RETRY = 5

// ---------------------------------------------------------------------------
// Handle offline state tracking and reconnection

// Tracking of connection status
type OfflineState = {
  offlineTimer: ReturnType<typeof setInterval> | null
  intervalTime: number
  retryNumber: number
}

const initializeOfflineState = (): OfflineState => ({
  offlineTimer: null,
  intervalTime: INIT_POLL_INTERVAL, // ms
  retryNumber: 0,
})

export function connectionHandlerFactory(
  healthUrl: string,
  syncKey: string,
  access: ReturnType<typeof createAccess>,
) {
  const state = initializeOfflineState()

  // ---------------------------------------------------------------------------
  // Server mutation functions
  async function addDocument(collectionPath: string, data: any) {
    return await access.addOne({
      collectionPath,
      data,
      // Any schema is used because the data was sanitized on original mutation
      schema: z.any(),
    })
  }

  async function updateDocument(
    collectionPath: string,
    docId: string,
    changes: DiffRecord[],
  ) {
    // Get a copy of latest doc from the server then apply all diffs
    return access
      .getOne({
        collectionPath,
        query: { uid: docId },
        schema: z.any(),
      })
      .then((res) =>
        res.error
          ? res
          : access.updateOne({
              collectionPath,
              data: diffApply(res.data, changes),
              query: { uid: docId },
              schema: z.any(),
            }),
      )
  }

  async function deleteDocument(collectionPath: string, docId: string) {
    return await access.deleteOne({
      collectionPath,
      query: { _id: docId },
    })
  }

  // ---------------------------------------------------------------------------

  /** Update the server with all local mutation records */
  async function updateServer() {
    try {
      const changelog = changelogSchema.safeParse(
        JSON.parse(localStorage.getItem(syncKey) ?? '{}'),
      )

      if (changelog.success) {
        // Apply each document change sequentially
        const serverMutations = Object.entries(changelog.data).flatMap(
          ([collectionPath, collectionChangeLog]) =>
            Object.entries(collectionChangeLog).map(([docId, record]) => {
              switch (record.action) {
                case MutationOp.Add: {
                  return {
                    operation: addDocument(collectionPath, record.value),
                    collectionPath,
                    docId,
                  }
                }
                case MutationOp.Update: {
                  return {
                    operation: updateDocument(
                      collectionPath,
                      docId,
                      record.value,
                    ),
                    collectionPath,
                    docId,
                  }
                }
                case MutationOp.Delete: {
                  return {
                    operation: deleteDocument(collectionPath, docId),
                    collectionPath,
                    docId,
                  }
                }
              }
            }),
        )

        for (const mutation of serverMutations) {
          const result = await mutation.operation

          if (result.error) {
            console.log(
              '[MONGOBASE-CLIENT]: Error applying pending changes',
              result,
            )
            console.error(result)
          }
          // Clear the pending mutation from the localStorage change log if the update was successful
          if (!result.error) {
            const collectionChangeLog =
              getLocalData(syncKey, mutation.collectionPath, z.any()) ?? {}
            delete collectionChangeLog[mutation.docId]

            if (Object.keys(collectionChangeLog).length === 0) {
              deleteLocalData(syncKey, mutation.collectionPath)
            } else {
              setLocalData(
                syncKey,
                mutation.collectionPath,
                collectionChangeLog,
              )
            }
          }
        }

        // Check for remaining changes and remove the connection poller
        const remainingChanges = JSON.parse(
          localStorage.getItem(syncKey) ?? '{}',
        )
        if (Object.keys(remainingChanges).length === 0) {
          state.retryNumber = 0
          state.offlineTimer = null
        } else {
          state.retryNumber += 1

          // we increase the interval time every time there is an error exponentially
          state.intervalTime =
            state.intervalTime < MAX_POLL_INTERVAL
              ? state.intervalTime * POLL_INTERVAL_GROWTH
              : state.intervalTime

          // If there was failures then we continue to poll
          // We stop retrying when we get to a threshold
          if (state.retryNumber > MAX_RETRY) {
            // Clear the queue so we don't try next time
            localStorage.removeItem(syncKey)
            state.retryNumber = 0
          } else {
            startConnectionPolling()
          }
        }
      } else {
        console.warn('Invalid or missing changelog.', changelog)
      }
    } catch (err) {
      console.error(err)
    }
  }

  /** Begin polling to detect reconnection when we can apply database changes again */
  function startConnectionPolling() {
    return setTimeout(async () => {
      const pollResult = await access.checkHealth(healthUrl)

      if (pollResult.status === 200) {
        console.log(
          'Connection restored. Attempting to apply pending changes...',
        )
        await updateServer()
      } else {
        // Only increase the increment if we are less than the max and the health check did not
        // fail because of a missing token
        state.intervalTime =
          state.intervalTime < MAX_POLL_INTERVAL && pollResult.status !== 511
            ? state.intervalTime * POLL_INTERVAL_GROWTH
            : state.intervalTime

        state.offlineTimer = startConnectionPolling()
      }
    }, state.intervalTime)
  }

  return {
    startConnectionPolling,
    connectionState: state,
  }
}

// ---------------------------------------------------------------------------
// Mutation record management
export type BaseDocument = object & { uid: string }

// Track an update mutation to a document
export function createUpdateRecord<T extends BaseDocument>(
  initial: T,
  current: T,
  collectionPath: string,
  syncKey: string,
) {
  const change = diff(initial, current)

  // Retrieve the existing collection change record or initialize it
  const changeLog =
    getLocalData(syncKey, collectionPath, collectionChangeRecordSchema) ?? {}

  const currentMutation = changeLog[current.uid]

  if (currentMutation) {
    switch (currentMutation.action) {
      // For a record that does not exist on the server we just update the pending add value
      case MutationOp.Add: {
        changeLog[current.uid] = {
          action: MutationOp.Update,
          value: current,
        }
        break
      }
      // Updates should not be able to be executed on a deleted record
      case MutationOp.Delete: {
        console.error('Mutation operation performed on a deleted document')
        break
      }
      // For sequential updates we save a new diff record to the diff array
      case MutationOp.Update: {
        changeLog[current.uid] = {
          action: MutationOp.Update,
          value: [...currentMutation.value, ...change],
        }
      }
    }
  } else {
    changeLog[current.uid] = {
      action: MutationOp.Update,
      value: change,
    }
  }

  setLocalData(syncKey, collectionPath, changeLog)
}

// Track a delete mutation to a document
export function createDeleteRecord(
  collectionPath: string,
  docId: string,
  syncKey: string,
) {
  // Retrieve the existing collection change record or initialize it
  const changeLog =
    getLocalData(syncKey, collectionPath, collectionChangeRecordSchema) ?? {}

  changeLog[docId] = {
    action: MutationOp.Delete,
    value: null,
  }

  setLocalData(syncKey, collectionPath, changeLog)
}

// Track an add operation to a document
export function createAddRecord<T extends BaseDocument>(
  collectionPath: string,
  value: T,
  syncKey: string,
) {
  // Retrieve the existing collection change record or initialize it
  const changeLog =
    getLocalData(syncKey, collectionPath, collectionChangeRecordSchema) ?? {}

  if (changeLog[value.uid]) {
    throw new Error(
      'Trying to add a document with the same UID as an existing document',
    )
  }

  changeLog[value.uid] = {
    action: MutationOp.Add,
    value,
  }

  setLocalData(syncKey, collectionPath, changeLog)
}
